import React, { useState, useEffect } from 'react';
import { loansAPI, loanTypesAPI, retailersAPI } from '../services/api';
import './AgentPages.css';

const LoanSubmission = () => {
  const [formData, setFormData] = useState({
    retailerId: '',
    loanTypeId: '',
    tenureDays: '60',    
    startDate: new Date().toISOString().split('T')[0],      
    purpose: '',
    requestedAmount: '',
  });
  const [loanTypes, setLoanTypes] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const [loanTypeRecover, setLoanTypeRecover] = useState(0);
  const [loanTypeComputedDaily, setLoanTypeComputedDaily] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setFetchLoading(true);
      setError(null);

      // Fetch both loan types and retailers
      const [typesResponse, retailersResponse] = await Promise.all([
        loanTypesAPI.getOptions(), // Use getOptions for loan types
        retailersAPI.list(), // Use list for retailers
      ]);
      
      // Handle response format properly
      let types = [];
      let retailersList = [];

      // Extract loan types data
      if (typesResponse) {
        if (Array.isArray(typesResponse)) {
          types = typesResponse;
        } else if (typesResponse.data && Array.isArray(typesResponse.data)) {
          types = typesResponse.data;
        } else if (typesResponse.loanTypes && Array.isArray(typesResponse.loanTypes)) {
          types = typesResponse.loanTypes;
        }
      }

      // Extract retailers data
      if (retailersResponse) {
        if (Array.isArray(retailersResponse)) {
          retailersList = retailersResponse;
        } else if (retailersResponse.data && Array.isArray(retailersResponse.data)) {
          retailersList = retailersResponse.data;
        } else if (retailersResponse.retailers && Array.isArray(retailersResponse.retailers)) {
          retailersList = retailersResponse.retailers;
        }
      }
      
      console.log('Loan Types:', types);
      console.log('Retailers:', retailersList);
      
      setLoanTypes(types || []);
      setRetailers(retailersList || []);

      if (types.length === 0) {
        setError('No loan types available. Please contact administrator.');
      }
      if (retailersList.length === 0) {
        setError('No retailers found. Please register retailers first.');
      }

    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load form data: ' + (err.message || 'Network error'));
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If loan type changes, auto-update tenure to match loan type
    if (name === 'loanTypeId' && value) {
      const selected = loanTypes.find(type => String(type.id) === String(value));
      if (selected) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          tenureDays: '60' // enforce fixed tenure of 60 days
        }));
        setSelectedLoanType(selected);

        // compute recover and daily emi based on loan type
        const recover = Number(selected.recover_amount || 0);
        setLoanTypeRecover(recover);
        const ltDaily = Number(selected.daily_emi || 0);
        let computedDaily = 0;
        if (ltDaily > 0) {
          computedDaily = ltDaily;
        } else if (recover > 0 && selected.tenure_days > 0) {
          computedDaily = Math.round(recover / Number(selected.tenure_days));
        }
        setLoanTypeComputedDaily(computedDaily);

        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Normalize required fields
    const normalizedStartDate = (() => {
      if (!formData.startDate) return new Date().toISOString().split('T')[0];
      const d = new Date(formData.startDate);
      return Number.isNaN(d.getTime())
        ? new Date().toISOString().split('T')[0]
        : d.toISOString().split('T')[0];
    })();
    const normalizedPurpose = String(formData.purpose || '').trim();

    // Basic validation
    const missingFields = [];
    if (!formData.retailerId) missingFields.push('retailerId');
    if (!formData.loanTypeId) missingFields.push('loanTypeId');
    if (!normalizedPurpose) missingFields.push('purpose');
    if (!normalizedStartDate) missingFields.push('startDate');
    // requestedAmount is required and must be > 0
    if (!formData.requestedAmount || Number(formData.requestedAmount) <= 0) missingFields.push('requestedAmount');

    if (missingFields.length > 0) {
      setError('Missing required fields: ' + missingFields.join(', '));
      return;
    }

    // Find selected loan type for validation
    const selectedLoanType = loanTypes.find(type => String(type.id) === String(formData.loanTypeId));
    if (!selectedLoanType) {
      setError('Please select a valid loan type');
      return;
    }

    // Enforce fixed tenure on server side; frontend uses fixed 60 days
    const tenureDays = 60;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const payload = {
        retailerId: formData.retailerId,
        loanTypeId: formData.loanTypeId,
        requestedAmount: Number(formData.requestedAmount),
        tenureDays,
        startDate: normalizedStartDate,
        purpose: normalizedPurpose
      };

      // DEBUG: log payload sent to server to help diagnose missing fields
      try {
        console.log('submitLoan payload:', JSON.stringify(payload));
      } catch (logErr) {
        console.warn('Failed to stringify submit payload', logErr);
      }

      const response = await loansAPI.submit(payload);
      console.log('Loan submission response:', response);
      
      setSuccess('Loan application submitted successfully! It will be reviewed by admin.');
      
      // Reset form
      setFormData({
        retailerId: '',
        loanTypeId: '',
        tenureDays: '60',
        startDate: '',
        purpose: '',
        requestedAmount: '',
      });
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      let errorMessage = 'Failed to submit loan application';
      if (err?.data?.message) errorMessage = err.data.message;
      else if (err?.data?.error) errorMessage = err.data.error;
      else if (err?.message) errorMessage = err.message;

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Submit Loan Application</h1>
          <p>Loading form data...</p>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loader"></div>
          <p>Loading retailers and loan types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Submit Loan Application</h1>
        <p>Submit a new loan application for a retailer</p>
      </div>

      <div className="form-container">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row top-row">
            <div className="form-group">
              <label>Retailer *</label>
              <select
                name="retailerId"
                value={formData.retailerId}
                onChange={handleInputChange}
                required
                disabled={retailers.length === 0}
              >
                <option value="">
                  {retailers.length === 0 ? 'No retailers available' : 'Select a Retailer'}
                </option>
                {retailers.map(retailer => (
                  <option key={retailer.id} value={retailer.id}>
                    {retailer.shop_name} - {retailer.name} ({retailer.phone})
                  </option>
                ))}
              </select>
              {retailers.length === 0 && (
                <small style={{ color: '#e74c3c' }}>
                  Please register retailers first before submitting loans.
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Loan Type *</label>
              <select
                name="loanTypeId"
                value={formData.loanTypeId}
                onChange={handleInputChange}
                required
                disabled={loanTypes.length === 0}
              >
                <option value="">
                  {loanTypes.length === 0 ? 'No loan types available' : 'Select Loan Type'}
                </option>
                {loanTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name} - ₹{type.min_amount?.toLocaleString()}-₹{type.max_amount?.toLocaleString()} ({type.tenure_days} days, recover ₹{type.recover_amount})
                  </option>
                ))}
              </select>
                {/* loan type cards moved below top row for left alignment */}
            </div>
            <div className="form-group">
              <label>Amount (₹) *</label>
              <input
                type="number"
                name="requestedAmount"
                value={formData.requestedAmount}
                onChange={handleInputChange}
                placeholder="Enter requested amount"
                min="1"
                step="1"
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Required — specify requested loan amount (must be greater than 0).
              </small>
            </div>
          </div>

          {selectedLoanType && (
            <div className="loan-type-cards">
              <div className="dashboard-card">
                <div className="card-icon">📈</div>
                <div className="card-content">
                  <div className="card-label">Recover Amount</div>
                  <div className="card-value">₹{(loanTypeRecover || 0).toLocaleString()}</div>
                  <div className="card-subtitle">Used to compute EMI</div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="card-icon">📅</div>
                <div className="card-content">
                  <div className="card-label">Daily EMI</div>
                  <div className="card-value">₹{(loanTypeComputedDaily || 0).toLocaleString()}</div>
                  <div className="card-subtitle">Based on recover / tenure</div>
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Tenure (Days)</label>
              <input
                type="number"
                name="tenureDays"
                value={formData.tenureDays}
                readOnly
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Fixed tenure: 60 days
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="form-group">
              <label>Purpose of Loan *</label>
              <textarea
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                required
                placeholder="Describe the purpose of this loan (e.g., inventory purchase, shop expansion)"
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            {(() => {
              const trimmedPurpose = String(formData.purpose || '').trim();
              const amtValid = formData.requestedAmount && Number(formData.requestedAmount) > 0;
              const canSubmit = !loading && retailers.length > 0 && loanTypes.length > 0 && formData.retailerId && formData.loanTypeId && formData.startDate && trimmedPurpose && amtValid;
              return (
                <button
                  type="submit"
                  className="button button-primary"
                  disabled={!canSubmit}
                >
                  {loading ? 'Submitting...' : 'Submit Loan Application'}
                </button>
              );
            })()}
            <button
              type="reset"
              className="button button-secondary"
              onClick={() => {
                setFormData({
                  retailerId: '',
                  loanTypeId: '',
                  tenureDays: '60',
                  startDate: new Date().toISOString().split('T')[0],
                  purpose: '',
                });
                setError(null);
                setSuccess(null);
              }}
            >
              Clear Form
            </button>
          </div>
        </form>

        {/* Display available loan types */}
        {loanTypes.length > 0 && (
          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>Available Loan Types</h3>
            <div className="grid grid-3">
              {loanTypes.map(type => (
                <div key={type.id} className="dashboard-card">
                  <div className="card-icon">💰</div>
                  <div className="card-content">
                    <div className="card-label">{type.name}</div>
                    <div className="card-value">₹{type.recover_amount}</div>
                    <div className="card-subtitle">Recover Amount (absolute)</div>
                    <div style={{fontSize: '12px', marginTop: '5px', color: '#666'}}>
                      Amount: ₹{type.min_amount?.toLocaleString()} - ₹{type.max_amount?.toLocaleString()}
                    </div>
                    <div style={{fontSize: '12px', color: '#666'}}>
                      Tenure: {type.tenure_days} days
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        {retailers.length === 0 && (
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <p>No retailers found. Register a retailer first:</p>
            <button 
              className="button button-secondary"
              onClick={() => window.location.href = '/register-retailer'}
            >
              Register Retailer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanSubmission;
